import {
  activateUserAccount,
  changeUserPassword,
  deactivateUserAccount,
  sendOTP,
  updateUserData,
  validateOtpMatch
} from '../stores/ajax';
import { PortletStoreApi } from '../App';
import type { CodedError, SuccessResponse } from 'ehs-react';
import {
  getErrorCodedException,
  getLocalizedErrorMessageBody,
  intl,
  showError
} from 'ehs-react';
import type { at } from '../generated/Beans';

type OtpSendDetails = at.sense.portlets.saf.patientselfonboarding.model.ui.OtpSendDetails;

enum PortletErrorCode {
  SessionExpired = 'PSOP00150',
  TooManyResendOperations = 'PSOP00160',
  TooManyOtpVerificationOperations = 'PSOP00170'
}

class CommunicationUtil {
  /**
   * Sends the authentication code to the user.
   * @param onSendingSuccess - callback when the authentication code was successfully sent
   * @param onSendingError - callback when there was an error while sending the authentication code
   */
  public static sendAuthenticationCode(
    onSendingSuccess: (sendDetails: OtpSendDetails) => void,
    onSendingError: (msg?: string, resendDisabled?: boolean) => void
  ): void {
    sendOTP({
      url: PortletStoreApi.getState().url
    })
      .then(p => {
        onSendingSuccess(p.data);
      })
      .catch(e => {
        const codedError = getErrorCodedException(e);
        if (
          codedError &&
          codedError.code === PortletErrorCode.TooManyResendOperations
        ) {
          onSendingError(getLocalizedErrorMessageBody(codedError), true);
        } else {
          CommunicationUtil.handleErrorCode(e);
          onSendingError();
        }
      });
  }

  /**
   * Validates the authentication code in the backend.
   * @param authenticationCode - the authentication code that was entered by the user
   * @param onCodeMatched - callback when the authentication code matched
   * @param onCodeMismatched - callback when the authentication code mismatched
   * @param onRequestStatusChanged - callback when the request pending status was changed
   */
  public static validateAuthenticationCode(
    authenticationCode: string,
    onCodeMatched: () => void,
    onCodeMismatched: (msg?: string) => void,
    onRequestStatusChanged: (pending: boolean) => void
  ): void {
    onRequestStatusChanged(true);
    validateOtpMatch({
      url: PortletStoreApi.getState().url,
      otp: authenticationCode
    })
      .then(p => {
        onRequestStatusChanged(false);
        if (p.data) {
          onCodeMatched();
        } else {
          onCodeMismatched();
        }
      })
      .catch(e => {
        onRequestStatusChanged(false);
        const codedError = getErrorCodedException(e) as CodedError;
        onCodeMismatched(getLocalizedErrorMessageBody(codedError));

        if (
          codedError.code !== PortletErrorCode.TooManyOtpVerificationOperations
        ) {
          CommunicationUtil.handleErrorCode(e);
        }
      });
  }

  /**
   * Updates user data in the backend.
   * @param firstName - first name of the user
   * @param lastName - last name of the user
   * @param onUpdateSuccess - callback when the user data was successfully updated
   * @param onRequestStatusChanged - callback when the request pending status was changed
   */
  public static sendUpdateUserDataRequest(
    firstName: string,
    lastName: string,
    onUpdateSuccess: () => void,
    onRequestStatusChanged: (pending: boolean) => void
  ): void {
    onRequestStatusChanged(true);
    updateUserData({
      url: PortletStoreApi.getState().url,
      firstName,
      lastName
    })
      .then(() => {
        onRequestStatusChanged(false);
        onUpdateSuccess();
      })
      .catch(e => {
        onRequestStatusChanged(false);
        CommunicationUtil.handleErrorCode(e);
      });
  }

  /**
   * Changes the password of a user account.
   * @param password - the new password of the user
   * @param onPasswordChangeSuccess - callback when the password was successfully changed
   * @param onPasswordChangeError - callback when the password change fails with an error message
   * @param onRequestStatusChanged - callback when the request pending status was changed
   */
  public static sendChangeUserPasswordRequest(
    password: string,
    onPasswordChangeSuccess: () => void,
    onPasswordChangeError: (message: string) => void,
    onRequestStatusChanged: (pending: boolean) => void
  ): void {
    onRequestStatusChanged(true);
    changeUserPassword({
      url: PortletStoreApi.getState().url,
      newPassword: password
    })
      .then((message: SuccessResponse<string>) => {
        onRequestStatusChanged(false);

        if (!!message.data[0]) {
          //message.data[0] is truthy only if something went wrong -> we show the message to the user
          onPasswordChangeError(message.data);
        } else {
          onPasswordChangeSuccess();
        }
      })
      .catch(e => {
        onRequestStatusChanged(false);
        CommunicationUtil.handleErrorCode(e);
      });
  }

  /**
   * Activates a user account.
   * @param onAccountActivationSuccess - callback when the account was successfully activated
   */
  public static sendActivateUserAccountRequest(
    onAccountActivationSuccess: () => void
  ): void {
    activateUserAccount({
      url: PortletStoreApi.getState().url
    })
      .then(() => {
        onAccountActivationSuccess();
      })
      .catch(e => {
        CommunicationUtil.handleErrorCode(e);
      });
  }

  /**
   * Deactivates a user account.
   */
  public static async sendDeactivateUserAccountRequest(): Promise<SuccessResponse<void> | void> {
    return deactivateUserAccount({
      url: PortletStoreApi.getState().url
    }).catch(e => {
      CommunicationUtil.handleErrorCode(e);
    });
  }

  private static handleErrorCode(e: unknown): void {
    const codedError = getErrorCodedException(e);
    if (codedError && codedError.code === PortletErrorCode.SessionExpired) {
      showError(e, {
        hideErrorDetails: true,
        buttons: [
          {
            text: intl('patientSelfOnboardingPortlet.messageBox.buttonReload'),
            variant: 'primary',
            onClick: () => location.reload()
          }
        ]
      });
    } else {
      showError(e, { hideErrorDetails: true });
    }
  }
}

export default CommunicationUtil;
